import React from 'react';
import { useTranslation } from 'react-i18next';
import { Switch } from 'antd';
import { useStore } from '../../store/store';
import { filterSameColor, iconButtonStyle } from '../../utils/styles';
import { NavbarButton } from '../Layout/NavbarButton';
import { NavbarInputNumber } from '../Layout/NavbarInputNumber';
import { NavbarListButton } from '../Layout/NavbarListButton';
import NavbarSelector from '../Layout/NavbarSelector';
import DuotoneLightIcon from '../common/DuotoneLightIcon';
import LabelledRow from '../common/LabelledRow';
import { ShapePanelSeparator } from './Shape';
import { SUB_SHAPE_COLOR } from './shapeUtils';
import {
  findLink, LINK_ANCHORS, LINK_MARKERS, LINK_TYPES, markerSize, markerSizes, mutateRemoveLink, mutateUpdateLink,
} from './shapeLinks';
import ShapePanelSections from './ShapePanelSections';

// Valeur du sélecteur pour une extrémité sans ancre fixée (placée automatiquement).
const AUTO_ANCHOR = 'auto';

const PANEL_INPUT_WIDTH = 120;

const selector = ({ updateBox, configuration }) => ({ updateBox, configuration });

// Réglages du lien sélectionné dans l'éditeur plein écran (cf.
// ShapeFullscreenEditor) : il remplace le panneau de la forme tant qu'un lien
// est sélectionné.
const ShapeLinkPanel = ({ box, ownerId, linkId, onEditText }) => {
  const { t } = useTranslation();
  const { updateBox, configuration } = useStore(selector);
  const link = findLink(box.content?.shapes, ownerId, linkId);
  if (!link) return null;

  const update = (mutator) => updateBox(box.id, (draft) => mutateUpdateLink(draft, ownerId, linkId, mutator));
  const colors = filterSameColor(configuration.colors.filter((c) => c.label !== 'transparent'));
  const markerOptions = LINK_MARKERS.map((marker) => ({ value: marker, label: t(`link-marker-${marker}`) }));
  const anchorOptions = [AUTO_ANCHOR, ...LINK_ANCHORS].map((anchor) => ({ value: anchor, label: t(`link-anchor-${anchor}`) }));
  const setAnchor = (field) => (anchor) => update((draft) => {
    if (anchor === AUTO_ANCHOR) delete draft[field];
    else draft[field] = anchor;
  });

  const selectRow = (label, value, onSelect, options, key) => (
    <LabelledRow label={label}>
      <NavbarSelector key={key} tooltipContent={label} onSelect={onSelect} selectedValue={value} searchable={false} options={options} />
    </LabelledRow>
  );
  const set = (field) => (value) => update((draft) => {
    draft[field] = value;
  });
  const sizes = markerSizes(link);

  // Une extrémité : son marqueur, et sa taille tant qu'il y en a un (vide =
  // proportionnelle à l'épaisseur du trait).
  const endSection = (end) => {
    const markerField = `${end}Marker`;
    const sizeField = `${end}MarkerSize`;
    return (
      <>
        {selectRow(t('link-marker'), link[markerField], set(markerField), markerOptions, `${end}MarkerSelector`)}
        {link[markerField] !== 'none' && (
        <LabelledRow label={t('link-marker-size')}>
          <NavbarInputNumber
            width={PANEL_INPUT_WIDTH}
            min={2}
            max={80}
            tooltipContent={t('link-marker-size')}
            value={Math.round(sizes[end])}
            onChange={(value) => {
              if (value == null) return;
              set(sizeField)(value);
            }}
            defaultValue={Math.round(markerSize(link.strokeWidth))}
          />
          <NavbarButton
            tooltipContent={t('link-marker-size-auto')}
            disabled={link[sizeField] == null}
            onClick={() => update((draft) => {
              delete draft[sizeField];
            })}
            icon={<DuotoneLightIcon name='arrow-rotate-left' style={iconButtonStyle(true)} />}
          />
        </LabelledRow>
        )}
      </>
    );
  };

  return (
    <>
      <ShapePanelSeparator title={t('shape-link')} color={SUB_SHAPE_COLOR} />
      <ShapePanelSections
        groupKey='shape-link'
        defaultOpenKeys={['line', 'start', 'end']}
        sections={[
          {
            key: 'line',
            title: t('link-section-line'),
            children: (
              <>
                {selectRow(t('type'), link.type, set('type'), LINK_TYPES.map((type) => ({ value: type, label: t(`link-type-${type}`) })), 'linkTypeSelector')}
                <LabelledRow label={t('link-color')}>
                  <NavbarListButton
                    tooltipContent={t('link-color')}
                    icon={<DuotoneLightIcon name='pen-line' style={iconButtonStyle(true)} />}
                    onListElementClick={(color) => set('color')(color.style)}
                    listElements={colors}
                    selectedElement={configuration.colors.find((c) => c.style === link.color)}
                    colorInIcon='primary'
                  />
                </LabelledRow>
                <LabelledRow label={t('link-thickness')}>
                  <NavbarInputNumber
                    width={PANEL_INPUT_WIDTH}
                    min={1}
                    max={20}
                    tooltipContent={t('link-thickness')}
                    value={link.strokeWidth}
                    onChange={(value) => {
                      if (value == null) return;
                      set('strokeWidth')(value);
                    }}
                    defaultValue={2}
                  />
                </LabelledRow>
                <LabelledRow label={t('link-dashed')}>
                  <Switch size='small' checked={!!link.dash} onChange={set('dash')} />
                </LabelledRow>
              </>
            ),
          },
          {
            key: 'anchors',
            title: t('link-section-anchors'),
            children: (
              <>
                {selectRow(t('link-start-anchor'), link.fromAnchor ?? AUTO_ANCHOR, setAnchor('fromAnchor'), anchorOptions, 'linkStartAnchorSelector')}
                {selectRow(t('link-end-anchor'), link.toAnchor ?? AUTO_ANCHOR, setAnchor('toAnchor'), anchorOptions, 'linkEndAnchorSelector')}
              </>
            ),
          },
          { key: 'start', title: t('link-section-start'), children: endSection('start') },
          { key: 'end', title: t('link-section-end'), children: endSection('end') },
          {
            key: 'text',
            title: t('link-text'),
            children: (
              // Libellé posé au milieu du lien (double-clic sur le lien, ou ici).
              <LabelledRow label={t('edit-link-text')}>
                <NavbarButton
                  tooltipContent={t('edit-link-text')}
                  onClick={onEditText}
                  icon={<DuotoneLightIcon name='pen-line' style={iconButtonStyle(true)} />}
                />
              </LabelledRow>
            ),
          },
        ]}
      />
      <LabelledRow label={t('delete-link')}>
        <NavbarButton
          tooltipContent={t('delete-link')}
          onClick={() => updateBox(box.id, (draft) => mutateRemoveLink(draft, ownerId, linkId))}
          icon={<DuotoneLightIcon name='trash' className='box-delete-icon' style={{ ...iconButtonStyle(), color: '#FF0000' }} />}
        />
      </LabelledRow>
    </>
  );
};

export default ShapeLinkPanel;
